import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ChevronLeft, Send, Paperclip, Clock, AlertTriangle, User, Crown, MoreVertical, ChevronRight, Check } from 'lucide-react'
import clsx from 'clsx'
import axios from 'axios'
import { buildApiUrl } from '../../../config/api'
import { toast } from '../../../components/Toast'
import { Card, Badge, Button, Input, Textarea, Select, Modal, ModalHeader, ModalBody, ModalFooter, DeleteConfirmModal } from '../../../components/ui'
import { formatDateTime, formatRelativeDate } from '../../../utils/dateUtils'
import { formatPhone } from '../../../utils/masks'

interface Demand {
  id: number; title: string; description: string | null; custom_fields: string | null
  status_code: string; status_label: string; status_color: string
  priority_code: string; priority_label: string; priority_color: string
  stage_id: number; stage_name: string; stage_color: string; max_days: number | null
  pipeline_id: number; pipeline_name: string
  category_name: string | null; category_fields: string | null
  voter_name: string | null; voter_phone: string | null; voter_email: string | null
  voter_neighborhood: string | null; voter_street: string | null; voter_number: string | null
  voter_city: string | null; voter_state: string | null
  leader_name: string | null; leader_phone: string | null
  creator_name: string | null
  due_date: string | null; stage_entered_at: string; completed_at: string | null
  is_success: boolean | null; created_at: string
}
interface Stage { id: number; name: string; color: string; is_final: boolean; completes_demand: boolean }
interface Assignee { id: number; name: string; email: string; role: string }
interface Comment { id: number; content: string; is_internal: boolean; author_name: string; created_at: string }
interface Attachment { id: number; file_url: string; file_name: string; file_size: number; uploaded_by: string; created_at: string }
interface HistoryItem { id: number; action: string; old_value: string | null; new_value: string | null; description: string | null; metadata: string | null; user_name: string | null; created_at: string }

const actionLabels: Record<string, string> = {
  created: 'Demanda criada', stage_changed: 'Etapa alterada', priority_changed: 'Prioridade alterada',
  assigned: 'Responsável adicionado', unassigned: 'Responsável removido', commented: 'Nota adicionado',
  attachment_added: 'Arquivo anexado', completed: 'Demanda concluída', cancelled: 'Demanda cancelada',
}

export default function DemandDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [demand, setDemand] = useState<Demand | null>(null)
  const [stages, setStages] = useState<Stage[]>([])
  const [assignees, setAssignees] = useState<Assignee[]>([])
  const [comments, setComments] = useState<Comment[]>([])
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const [history, setHistory] = useState<HistoryItem[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'dados' | 'atividade'>('dados')
  const [newComment, setNewComment] = useState('')
  const [commentFiles, setCommentFiles] = useState<File[]>([])
  const [sendingComment, setSendingComment] = useState(false)
  const [cancelModalOpen, setCancelModalOpen] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [actionsOpen, setActionsOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [editForm, setEditForm] = useState({ title: '', description: '', priority_type_id: '', due_date: '' })
  const [saving, setSaving] = useState(false)
  const [assigneeModalOpen, setAssigneeModalOpen] = useState(false)
  const [orgUsers, setOrgUsers] = useState<{ id: number; name: string }[]>([])
  const [selectedUserId, setSelectedUserId] = useState('')
  const [serviceFields, setServiceFields] = useState<Record<string, any>>({})
  const [savingFields, setSavingFields] = useState(false)

  useEffect(() => { if (id) fetchDemand() }, [id])

  async function fetchDemand() {
    setLoading(true)
    try {
      const res = await axios.get(buildApiUrl(`/v1/demands/${id}`))
      const d = res.data.data
      setDemand(d.demand)
      setStages(d.stages || [])
      setAssignees(d.assignees || [])
      setComments(d.comments || [])
      setAttachments(d.attachments || [])
      setHistory(d.history || [])
      setServiceFields(d.demand.custom_fields ? JSON.parse(d.demand.custom_fields) : {})
    } catch { /* */ } finally { setLoading(false) }
  }

  async function changeStage(stageId: number) {
    try {
      await axios.put(buildApiUrl(`/v1/demands/${id}/stage`), { stage_id: stageId })
      toast('success', 'Etapa atualizada')
      fetchDemand()
    } catch (err: any) { toast('error', err.response?.data?.error || 'Erro ao mover') }
  }

  async function sendComment() {
    if ((!newComment.trim() && commentFiles.length === 0) || sendingComment) return
    setSendingComment(true)
    try {
      if (newComment.trim()) {
        await axios.post(buildApiUrl(`/v1/demands/${id}/comments`), { content: newComment })
      }
      for (const file of commentFiles) {
        const formData = new FormData()
        formData.append('file', file)
        await axios.post(buildApiUrl(`/v1/demands/${id}/attachments`), formData)
      }
      setNewComment('')
      setCommentFiles([])
      fetchDemand()
    } catch { toast('error', 'Erro ao enviar') } finally { setSendingComment(false) }
  }

  function addCommentFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files
    if (!files || files.length === 0) return
    const newFiles = Array.from(files)
    setCommentFiles(prev => [...prev, ...newFiles])
    // Reset input
    e.target.value = ''
  }

  function removeCommentFile(index: number) {
    setCommentFiles(prev => prev.filter((_, i) => i !== index))
  }

  async function handleCancel() {
    try {
      await axios.put(buildApiUrl(`/v1/demands/${id}/cancel`), { reason: cancelReason })
      toast('success', 'Demanda cancelada')
      setCancelModalOpen(false)
      fetchDemand()
    } catch (err: any) { toast('error', err.response?.data?.error || 'Erro') }
  }

  async function saveServiceFields() {
    if (savingFields) return
    setSavingFields(true)
    try {
      await axios.put(buildApiUrl(`/v1/demands/${id}`), {
        title: demand!.title, description: demand!.description,
        priority_type_id: demand!.priority_code === 'LOW' ? 1 : demand!.priority_code === 'NORMAL' ? 2 : demand!.priority_code === 'HIGH' ? 3 : 4,
        custom_fields: serviceFields,
      })
      toast('success', 'Atendimento salvo')
    } catch { toast('error', 'Erro ao salvar') } finally { setSavingFields(false) }
  }

  function openEdit() {
    if (!demand) return
    const priorityMap: Record<string, string> = { LOW: '1', NORMAL: '2', HIGH: '3', URGENT: '4' }
    setEditForm({ title: demand.title, description: demand.description || '', priority_type_id: priorityMap[demand.priority_code] || '2', due_date: demand.due_date || '' })
    setEditModalOpen(true)
  }

  async function handleEdit() {
    if (saving) return
    setSaving(true)
    try {
      await axios.put(buildApiUrl(`/v1/demands/${id}`), {
        title: editForm.title, description: editForm.description || null,
        priority_type_id: Number(editForm.priority_type_id), due_date: editForm.due_date || null,
      })
      toast('success', 'Demanda atualizada')
      setEditModalOpen(false)
      fetchDemand()
    } catch (err: any) { toast('error', err.response?.data?.error || 'Erro') } finally { setSaving(false) }
  }

  const daysInStage = demand?.stage_entered_at ? Math.floor((Date.now() - new Date(demand.stage_entered_at).getTime()) / 86400000) : null
  const isOverdue = daysInStage != null && demand?.max_days != null && demand.max_days > 0 && daysInStage > demand.max_days
  const customFields = demand?.custom_fields ? JSON.parse(demand.custom_fields) : {}
  const categoryFields = demand?.category_fields ? JSON.parse(demand.category_fields) : []
  const entryFields = categoryFields.filter((f: any) => !f.stage || f.stage === 'entry')
  const closingFields = categoryFields.filter((f: any) => f.stage === 'closing')

  if (loading) return (
    <div className="space-y-6 animate-pulse">
      <div className="h-8 w-64 bg-gray-200 rounded" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4"><div className="h-40 bg-gray-100 rounded-2xl" /><div className="h-60 bg-gray-100 rounded-2xl" /></div>
        <div className="space-y-4"><div className="h-32 bg-gray-100 rounded-2xl" /><div className="h-48 bg-gray-100 rounded-2xl" /></div>
      </div>
    </div>
  )

  if (!demand) return <div className="text-center py-20 text-gray-500">Demanda não encontrada</div>

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/demands')} className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-gray-200/60 text-gray-400 hover:text-gray-600 transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <h1 className="text-xl font-bold text-gray-900">Demanda</h1>
              {isOverdue && <span className="flex items-center gap-1 text-[10px] text-error-500 font-semibold bg-error-50 px-1.5 py-0.5 rounded-full"><AlertTriangle className="w-3 h-3" /> Atrasada</span>}
            </div>
            <p className="text-sm text-gray-500">{demand.title}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-lg" style={{ backgroundColor: demand.priority_color + '15', color: demand.priority_color }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: demand.priority_color }} />{demand.priority_label}
          </span>
          {demand.status_code === 'OPEN' && (
            <div className="relative">
              <button onClick={() => setActionsOpen(!actionsOpen)} className="h-9 w-9 flex items-center justify-center rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-50">
                <MoreVertical className="w-4 h-4" />
              </button>
              {actionsOpen && (<>
                <div className="fixed inset-0 z-10" onClick={() => setActionsOpen(false)} />
                <div className="absolute right-0 top-10 z-20 w-40 bg-white rounded-xl shadow-lg border border-gray-200 py-1">
                  <button onClick={() => { setActionsOpen(false); openEdit() }} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">Editar</button>
                  <button onClick={() => { setActionsOpen(false); setCancelModalOpen(true) }} className="w-full text-left px-3 py-2 text-sm text-error-500 hover:bg-error-50">Cancelar demanda</button>
                </div>
              </>)}
            </div>
          )}
        </div>
      </div>

      {/* Pipeline stepper */}
      {demand.status_code === 'OPEN' && stages.length > 0 && (
        <div className="flex items-center overflow-x-auto py-1">
          {stages.map((s, i) => {
            const currentIndex = stages.findIndex(st => st.id === demand.stage_id)
            const isPast = i < currentIndex
            const isCurrent = s.id === demand.stage_id
            return (
              <div key={s.id} className="flex items-center flex-shrink-0">
                <button onClick={() => changeStage(s.id)} className="flex items-center gap-2 group">
                  <div className={clsx('w-7 h-7 rounded-full flex items-center justify-center transition-all flex-shrink-0',
                    isCurrent ? 'text-white' : isPast ? 'text-white' : 'text-gray-400 bg-gray-100 group-hover:bg-gray-200'
                  )} style={isCurrent ? { backgroundColor: s.color, boxShadow: `0 0 0 4px ${s.color}30` } : isPast ? { backgroundColor: s.color + 'cc' } : undefined}>
                    {isPast ? <Check className="w-3.5 h-3.5" /> : <span className="text-[11px] font-bold">{i + 1}</span>}
                  </div>
                  <span className={clsx('text-xs font-medium transition-colors',
                    isCurrent ? 'text-gray-900' : isPast ? 'text-gray-600' : 'text-gray-400 group-hover:text-gray-600'
                  )}>{s.name}</span>
                </button>
                {i < stages.length - 1 && (
                  <div className={clsx('w-8 h-0.5 mx-2 rounded-full', isPast ? 'bg-gray-300' : 'bg-gray-100')} />
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-6 border-b border-gray-200">
        {[
          { id: 'dados' as const, label: 'Dados' },
          { id: 'atividade' as const, label: `Atividade (${comments.length + history.filter(h => h.action !== 'commented').length})` },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={clsx('pb-2.5 text-sm font-medium transition-colors border-b-2 -mb-px', activeTab === tab.id ? 'text-brand-600 border-brand-500' : 'text-gray-500 border-transparent hover:text-gray-700')}>
            {tab.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Conteúdo principal */}
        <div className="lg:col-span-2 space-y-4">
          {/* Tab Dados */}
          {activeTab === 'dados' && (<>
            <Card>
              {/* Informações gerais */}
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Informações de cadastro</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-8 gap-y-3 pb-3">
                <div><p className="text-xs text-gray-400 mb-0.5">Título</p><p className="text-sm text-gray-900">{demand.title}</p></div>
                <div><p className="text-xs text-gray-400 mb-0.5">Funil</p><p className="text-sm text-gray-800">{demand.pipeline_name}</p></div>
                {demand.category_name && <div><p className="text-xs text-gray-400 mb-0.5">Categoria</p><p className="text-sm text-gray-800">{demand.category_name}</p></div>}
                {demand.completed_at && <div><p className="text-xs text-gray-400 mb-0.5">Finalizado em</p><p className="text-sm text-gray-800">{formatDateTime(demand.completed_at)}</p></div>}
              </div>

              {/* Descrição */}
              {demand.description && (
                <div className="pt-3 pb-5 border-b border-gray-100">
                  <p className="text-xs text-gray-400 mb-0.5">Descrição</p>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{demand.description}</p>
                </div>
              )}

              {/* Solicitante */}
              {demand.voter_name && (
                <div className="py-5 border-b border-gray-100">
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Solicitante</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-8 gap-y-3">
                    <div><p className="text-xs text-gray-400 mb-0.5">Nome</p><p className="text-sm text-gray-900">{demand.voter_name}</p></div>
                    {demand.voter_phone && <div><p className="text-xs text-gray-400 mb-0.5">Telefone</p><p className="text-sm text-gray-800">{formatPhone(demand.voter_phone)}</p></div>}
                    {demand.voter_email && <div><p className="text-xs text-gray-400 mb-0.5">E-mail</p><p className="text-sm text-gray-800">{demand.voter_email}</p></div>}
                    {demand.voter_city && <div><p className="text-xs text-gray-400 mb-0.5">Cidade</p><p className="text-sm text-gray-800">{demand.voter_city}/{demand.voter_state}</p></div>}
                    {demand.voter_neighborhood && <div><p className="text-xs text-gray-400 mb-0.5">Bairro</p><p className="text-sm text-gray-800">{demand.voter_neighborhood}</p></div>}
                    {(demand.voter_street || demand.voter_number) && <div><p className="text-xs text-gray-400 mb-0.5">Endereço</p><p className="text-sm text-gray-800">{[demand.voter_street, demand.voter_number].filter(Boolean).join(', ')}</p></div>}
                  </div>
                </div>
              )}

              {/* Anexos */}
              <div className="pt-5">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Anexos</h4>
                  <label className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 font-medium cursor-pointer transition-colors">
                    <Paperclip className="w-3.5 h-3.5" /> Adicionar
                    <input type="file" multiple onChange={async (e) => {
                      if (!e.target.files?.length) return
                      for (const file of Array.from(e.target.files)) {
                        const formData = new FormData()
                        formData.append('file', file)
                        await axios.post(buildApiUrl(`/v1/demands/${id}/attachments`), formData)
                      }
                      toast('success', 'Arquivo(s) anexado(s)')
                      fetchDemand()
                      e.target.value = ''
                    }} className="hidden" />
                  </label>
                </div>
                {attachments.length > 0 ? (
                  <div className="space-y-2">
                    {attachments.map(a => (
                      <a key={a.id} href={a.file_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                        <Paperclip className="w-4 h-4 text-gray-400" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-brand-600 truncate">{a.file_name}</p>
                          <p className="text-xs text-gray-400">{a.uploaded_by} · {formatRelativeDate(a.created_at)}</p>
                        </div>
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400">Nenhum anexo</p>
                )}
              </div>

              {/* Registro */}
              <div className="pt-5 border-t border-gray-100">
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Registro</h4>
                <div className="flex items-center gap-4 text-[11px] text-gray-400">
                  {demand.creator_name && <span>Criado por {demand.creator_name}</span>}
                  <span>{formatDateTime(demand.created_at)}</span>
                  {demand.completed_at && <span>· Finalizado em {formatDateTime(demand.completed_at)}</span>}
                </div>
              </div>
            </Card>

            {/* Card Campos do cadastro (somente leitura) */}
            {entryFields.length > 0 && (
              <Card>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Campos do cadastro</h3>
                <div className="space-y-3">
                  {entryFields.map((field: any) => (
                    <div key={field.key}>
                      <p className="text-xs text-gray-400 mb-0.5">{field.label}</p>
                      <p className="text-sm text-gray-900">
                        {field.type === 'boolean'
                          ? (customFields[field.key] ? 'Sim' : 'Não')
                          : customFields[field.key] || '—'}
                      </p>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Card Atendimento (campos de encerramento, editáveis) */}
            {closingFields.length > 0 && (
              <Card>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Atendimento</h3>
                  <Button size="sm" variant="outline" onClick={saveServiceFields} loading={savingFields}>Salvar</Button>
                </div>
                <div className="space-y-4">
                  {closingFields.map((field: any) => (
                    <div key={field.key}>
                      {field.type === 'text' && (
                        <Input label={field.label} value={serviceFields[field.key] || ''}
                          onChange={e => setServiceFields(f => ({ ...f, [field.key]: e.target.value }))} />
                      )}
                      {field.type === 'number' && (
                        <Input label={field.label} type="number" value={serviceFields[field.key] || ''}
                          onChange={e => setServiceFields(f => ({ ...f, [field.key]: e.target.value }))} />
                      )}
                      {field.type === 'date' && (
                        <Input label={field.label} type="date" value={serviceFields[field.key] || ''}
                          onChange={e => setServiceFields(f => ({ ...f, [field.key]: e.target.value }))} />
                      )}
                      {field.type === 'textarea' && (
                        <Textarea label={field.label} value={serviceFields[field.key] || ''}
                          onChange={e => setServiceFields(f => ({ ...f, [field.key]: e.target.value }))} rows={2} />
                      )}
                      {field.type === 'select' && (
                        <Select label={field.label} value={serviceFields[field.key] || ''}
                          onChange={e => setServiceFields(f => ({ ...f, [field.key]: e.target.value }))}
                          options={(field.options || []).map((o: string) => ({ value: o, label: o }))} placeholder="Selecione" />
                      )}
                      {field.type === 'boolean' && (
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input type="checkbox" checked={!!serviceFields[field.key]}
                            onChange={e => setServiceFields(f => ({ ...f, [field.key]: e.target.checked }))}
                            className="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500" />
                          <span className="text-sm text-gray-700">{field.label}</span>
                        </label>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </>)}

          {/* Tab Atividade */}
          {activeTab === 'atividade' && (
            <Card>
              {/* Novo comentário */}
              <div className="mb-6 pb-6 border-b border-gray-100">
                <div className="bg-gray-50 rounded-xl p-3">
                  <textarea value={newComment} onChange={e => setNewComment(e.target.value)} placeholder="Escreva uma nota..."
                    rows={3} className="w-full bg-transparent text-sm placeholder:text-gray-400 outline-none resize-none" />

                  <div className="flex items-center justify-end mt-2 pt-2 border-t border-gray-200/50">
                    <Button size="sm" onClick={sendComment} loading={sendingComment} disabled={!newComment.trim()}>
                      <Send className="w-3.5 h-3.5 mr-1.5" /> Enviar
                    </Button>
                  </div>
                </div>
              </div>

              {/* Timeline */}
              {comments.length === 0 && history.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">Nenhuma atividade registrada</p>
              ) : (
                <div className="space-y-4">
                  {/* Merge comments + history by date */}
                  {[
                    ...comments.map(c => ({ type: 'comment' as const, date: c.created_at, data: c })),
                    ...history.filter(h => h.action !== 'commented').map(h => ({ type: 'history' as const, date: h.created_at, data: h })),
                  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map((item, i) => (
                    <div key={`${item.type}-${i}`} className="flex gap-3">
                      <div className={clsx('w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5',
                        item.type === 'comment' ? 'bg-brand-50' : 'bg-gray-100')}>
                        {item.type === 'comment' ? <User className="w-3.5 h-3.5 text-brand-500" /> : <Clock className="w-3.5 h-3.5 text-gray-400" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        {item.type === 'comment' ? (
                          <div>
                            <p className="text-sm text-gray-800 font-medium mb-0.5">Nota</p>
                            <p className="text-sm text-gray-600 whitespace-pre-wrap">{(item.data as Comment).content}</p>
                            <p className="text-[11px] text-gray-400 mt-1">{(item.data as Comment).author_name} · {formatRelativeDate(item.date)}</p>
                          </div>
                        ) : (() => {
                          const h = item.data as HistoryItem
                          return (
                            <div>
                              <p className="text-sm text-gray-800 font-medium">
                                {actionLabels[h.action] || h.action}
                                {h.old_value && h.new_value && (
                                  <>: <span className="text-gray-400 font-normal">{h.old_value}</span> <span className="text-gray-400 font-normal">→</span> <span className="text-brand-600">{h.new_value}</span></>
                                )}
                                {h.new_value && !h.old_value && <>: <span className="text-brand-600">{h.new_value}</span></>}
                                {h.description && !h.old_value && !h.new_value && <span className="font-normal text-gray-500">: {h.description}</span>}
                              </p>
                              <p className="text-[11px] text-gray-400 mt-0.5">
                                {h.user_name && <>{h.user_name} · </>}{formatRelativeDate(item.date)}
                              </p>
                            </div>
                          )
                        })()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Liderança */}
          {demand.leader_name && (
            <Card>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Liderança</h3>
              <div>
                <p className="text-sm font-medium text-gray-800">{demand.leader_name}</p>
                {demand.leader_phone && <p className="text-xs text-gray-400">{formatPhone(demand.leader_phone)}</p>}
              </div>
            </Card>
          )}

          {/* Responsáveis */}
          <Card>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Responsáveis</h3>
              <button onClick={() => {
                if (orgUsers.length === 0) {
                  axios.get(buildApiUrl('/v1/organization-users')).then(r => setOrgUsers(r.data.data.map((u: any) => ({ id: u.id, name: u.name })))).catch(() => {})
                }
                setSelectedUserId('')
                setAssigneeModalOpen(true)
              }} className="text-xs text-gray-400 hover:text-gray-600 font-medium transition-colors">+ Adicionar</button>
            </div>
            {assignees.length === 0 ? (
              <p className="text-xs text-gray-400">Nenhum responsável</p>
            ) : (
              <div className="space-y-2">
                {assignees.map(a => (
                  <div key={a.id} className="flex items-center justify-between group">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-brand-50 flex items-center justify-center flex-shrink-0">
                        <span className="text-[10px] font-bold text-brand-600">{a.name.charAt(0)}</span>
                      </div>
                      <div>
                        <p className="text-sm text-gray-800">{a.name}</p>
                        <p className="text-[10px] text-gray-400">{a.role === 'owner' ? 'Responsável' : 'Participante'}</p>
                      </div>
                    </div>
                    <button onClick={async () => {
                      await axios.delete(buildApiUrl(`/v1/demands/${id}/assignees/${a.id}`))
                      toast('success', 'Removido')
                      fetchDemand()
                    }} className="hidden group-hover:flex h-6 w-6 items-center justify-center rounded text-gray-400 hover:text-error-500 hover:bg-error-50 transition-colors">
                      <span className="text-xs">×</span>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Andamento */}
          <Card>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Andamento</h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-gray-400 mb-1">Etapa atual</p>
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: demand.stage_color }} />
                  <span className="text-sm font-medium text-gray-800">{demand.stage_name}</span>
                </div>
              </div>
              {demand.due_date && (
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Prazo</p>
                  <p className="text-sm text-gray-800">{demand.due_date}</p>
                </div>
              )}
              {daysInStage != null && (() => {
                const maxDays = demand.max_days && demand.max_days > 0 ? demand.max_days : null
                const pct = maxDays ? Math.min(Math.round((daysInStage / maxDays) * 100), 100) : null
                const barColor = isOverdue ? '#ef4444' : pct != null && pct >= 80 ? '#f59e0b' : '#22c55e'
                return (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs text-gray-400">Tempo na etapa</p>
                      <p className={clsx('text-xs font-medium', isOverdue ? 'text-error-500' : 'text-gray-600')}>
                        {daysInStage} dia{daysInStage !== 1 ? 's' : ''}{maxDays ? ` de ${maxDays}` : ''}
                      </p>
                    </div>
                    {maxDays && (
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: barColor }} />
                      </div>
                    )}
                    {isOverdue && <p className="text-[10px] text-error-500 font-medium mt-1">Prazo excedido</p>}
                  </div>
                )
              })()}
            </div>
          </Card>
        </div>
      </div>

      {/* Modal editar */}
      <Modal open={editModalOpen} onClose={() => setEditModalOpen(false)} size="md">
        <ModalHeader onClose={() => setEditModalOpen(false)}>Editar Demanda</ModalHeader>
        <ModalBody>
          <div className="space-y-4">
            <Input label="Título" value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} />
            <Textarea label="Descrição" value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} rows={3} />
            <Select label="Prioridade" value={editForm.priority_type_id} onChange={e => setEditForm(f => ({ ...f, priority_type_id: e.target.value }))}
              options={[{ value: '1', label: 'Baixa' }, { value: '2', label: 'Normal' }, { value: '3', label: 'Alta' }, { value: '4', label: 'Urgente' }]} />
            <Input label="Prazo" type="date" value={editForm.due_date} onChange={e => setEditForm(f => ({ ...f, due_date: e.target.value }))} />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setEditModalOpen(false)}>Cancelar</Button>
          <Button onClick={handleEdit} loading={saving}>Salvar</Button>
        </ModalFooter>
      </Modal>

      {/* Modal adicionar responsável */}
      <Modal open={assigneeModalOpen} onClose={() => setAssigneeModalOpen(false)} size="sm">
        <ModalHeader onClose={() => setAssigneeModalOpen(false)}>Adicionar responsável</ModalHeader>
        <ModalBody>
          <Select label="Usuário" value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)}
            options={orgUsers.filter(u => !assignees.some(a => a.id === u.id)).map(u => ({ value: String(u.id), label: u.name }))}
            placeholder="Selecione o responsável" />
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setAssigneeModalOpen(false)}>Cancelar</Button>
          <Button disabled={!selectedUserId} onClick={async () => {
            try {
              await axios.post(buildApiUrl(`/v1/demands/${id}/assignees`), { user_id: Number(selectedUserId) })
              toast('success', 'Responsável adicionado')
              setAssigneeModalOpen(false)
              fetchDemand()
            } catch { toast('error', 'Erro ao adicionar') }
          }}>Adicionar</Button>
        </ModalFooter>
      </Modal>

      {/* Modal cancelar */}
      <Modal open={cancelModalOpen} onClose={() => setCancelModalOpen(false)} size="sm">
        <ModalHeader onClose={() => setCancelModalOpen(false)}>Cancelar Demanda</ModalHeader>
        <ModalBody>
          <p className="text-sm text-gray-600 mb-4">Tem certeza que deseja cancelar esta demanda?</p>
          <Textarea label="Motivo (opcional)" value={cancelReason} onChange={e => setCancelReason(e.target.value)} rows={2} placeholder="Descreva o motivo do cancelamento..." />
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setCancelModalOpen(false)}>Voltar</Button>
          <Button variant="danger" onClick={handleCancel}>Cancelar demanda</Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
